import { environment } from './../../environments/environment';
import { LoginRes } from './../../modules/dashboard/dto/login/login.res';

interface ReqInit<Body> extends Omit<RequestInit, 'body'> {
  body?: Body;
  query?: Record<string, string>;
}

interface ApiError extends Error {
  status?: number;
  data?: unknown;
}

function getBaseUrlCandidates(base: string | URL | undefined) {
  const candidates = new Set<string>();

  if (base) {
    candidates.add(String(base));
  }

  if (environment.endpoint) {
    candidates.add(String(environment.endpoint));
  }

  if (typeof window !== 'undefined') {
    if (['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      candidates.add('http://localhost:3500/');
      candidates.add('http://127.0.0.1:3500/');
    } else {
      candidates.add(window.location.origin);
    }
  }

  return Array.from(candidates);
}

function isApiPath(path: string) {
  return path.startsWith('api/') || path.startsWith('/api/');
}

function isHtmlResponse(response: Response) {
  const contentType = response.headers.get('content-type') || '';
  return contentType.includes('text/html');
}

export function getUrl(path: string | URL, base: string | URL | undefined, query?: ReqInit<any>['query'], baseIndex = 0) {
  const baseCandidates = getBaseUrlCandidates(base);
  const fallbackBase = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3500/';
  const url = new URL(path, baseCandidates[baseIndex] || fallbackBase);

  const queryEntries = Object.entries(query || {});

  for (let i = 0; i < queryEntries.length; i += 1) {
    url.searchParams.set(queryEntries[i][0], queryEntries[i][1]);
  }

  return url;
}

async function requestWithFallback(path: string, init: RequestInit, query?: ReqInit<any>['query']) {
  const baseCandidates = getBaseUrlCandidates(environment.endpoint);
  const fallbackBase = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3500/';
  const candidates = baseCandidates.length ? baseCandidates : [fallbackBase];
  const apiRequest = isApiPath(path);
  let lastError: unknown;

  for (let index = 0; index < candidates.length; index += 1) {
    try {
      const response = await fetch(getUrl(path, candidates[index], query), init);

      if (!apiRequest || !isHtmlResponse(response)) {
        return response;
      }

      lastError = new Error(`Invalid API response received from ${candidates[index]}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
}

export function getBody(init?: ReqInit<any>) {
  return init?.body && !(init?.body instanceof FormData) ? JSON.stringify(init?.body) : init?.body;
}

export function getUser(): LoginRes | null {
  return JSON.parse(String(localStorage.getItem('user')));
}

export function getAuthHeader(): Record<string, string> {
  const user = getUser();
  return user ? { Authorization: `Bearer ${user.token}` } : {};
}

function getErrorMessageFromData(data: unknown, fallback: string) {
  if (typeof data === 'string') {
    return data || fallback;
  }

  if (data && typeof data === 'object') {
    const maybeMessage = (data as Record<string, unknown>).message;
    if (typeof maybeMessage === 'string' && maybeMessage) {
      return maybeMessage;
    }

    const maybeError = (data as Record<string, unknown>).error;
    if (typeof maybeError === 'string' && maybeError) {
      return maybeError;
    }

    if (maybeError && typeof maybeError === 'object') {
      const nestedMessage = (maybeError as Record<string, unknown>).message;
      if (typeof nestedMessage === 'string' && nestedMessage) {
        return nestedMessage;
      }
    }

    const maybeCode = (data as Record<string, unknown>).code;
    if (typeof maybeCode === 'string' && maybeCode) {
      return maybeCode;
    }
  }

  return fallback;
}

async function createApiError(res: Response, fallback: string): Promise<ApiError> {
  let data: unknown;

  try {
    data = await res.clone().json();
  } catch {
    try {
      data = await res.clone().text();
    } catch {
      data = undefined;
    }
  }

  const error = new Error(getErrorMessageFromData(data, fallback)) as ApiError;
  error.status = res.status;
  error.data = data;
  return error;
}

export function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    return getErrorMessageFromData((error as ApiError).data ?? error, fallback);
  }

  return fallback;
}

export async function fget<B = any, R = any>(path: string, init?: ReqInit<B>) {
  const res = await requestWithFallback(
    path,
    {
      ...init,
      method: 'GET',
      headers: {
        ...getAuthHeader(),
        ...init?.headers,
      },
    } as any,
    init?.query,
  );

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<R>]);
  }

  throw await createApiError(res, 'Request failed');
}

export async function fpost<Body = any, Resp = any>(path: string, init?: ReqInit<Body>) {
  const res = await requestWithFallback(
    path,
    {
      ...init,
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...init?.headers,
      },
      body: getBody(init),
    } as any,
    init?.query,
  );

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<Resp>]);
  }

  throw await createApiError(res, 'Request failed');
}

export async function fput<B = any, R = any>(path: string, init?: ReqInit<B>) {
  const res = await requestWithFallback(
    path,
    {
      ...init,
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...init?.headers,
      },
      body: getBody(init),
    } as any,
    init?.query,
  );

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<R>]);
  }

  throw await createApiError(res, 'Request failed');
}

export async function fpatch<B = any, R = any>(path: string, init?: ReqInit<B>) {
  const res = await requestWithFallback(
    path,
    {
      ...init,
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...init?.headers,
      },
      body: getBody(init),
    } as any,
    init?.query,
  );

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<R>]);
  }

  throw await createApiError(res, 'Request failed');
}

export async function fdelete<B = any, R = any>(path: string, init?: ReqInit<B>) {
  const res = await requestWithFallback(
    path,
    {
      ...init,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...init?.headers,
      },
      body: getBody(init),
    } as any,
    init?.query,
  );

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<R>]);
  }

  throw await createApiError(res, 'Request failed');
}

export async function fupload<B = any, R = any>(path: string | URL, init?: ReqInit<B>) {
  const res = await fetch(path, {
    ...init,
    method: 'POST',
    headers: {
      Accept: 'application/json',
      ...getAuthHeader(),
      ...init?.headers,
    },
  } as any);

  if (res.ok) {
    return Promise.all([res.clone().text(), res.json() as Promise<R>]);
  }

  throw await createApiError(res, 'Request failed');
}
